import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import isMobile from '../util/mobile.js';
import { enableBodyScroll, disableBodyScroll } from '../util/bodyScroll.js';

import Passengers from './Passengers.jsx';

const URL_PARAM = 'flightpassengers=overlay';

export class FlightPassengers extends React.PureComponent {
    constructor(props) {
        super(props);

        this.toggle = this.toggle.bind(this);
        this.isPassengerContainerStillOnFocus = this.isPassengerContainerStillOnFocus.bind(this);
        this.passengerContainerOnClick = this.passengerContainerOnClick.bind(this);
        this.transitionOverlay = this.transitionOverlay.bind(this);

        this.state = {
            listVisible: window.location.search === `?${URL_PARAM}`,
        };

        window.addEventListener('popstate', this.transitionOverlay);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.transitionOverlay);
    }

    transitionOverlay() {
        if (isMobile()) {
            if (window.history.state == null) {
                // This means it's page load
                return;
            }
            this.setState({ listVisible: window.location.search === `?${URL_PARAM}` });
        }
    }

    toggle() {
        if (this.state.listVisible) {
            enableBodyScroll();
            if (isMobile() && !this.props.showPassengerWarning && this.props.popUrlContext) {
                this.props.popUrlContext();
            }
            if (!isMobile() && !this.props.showPassengerWarning) {
                window.removeEventListener('click', this.isPassengerContainerStillOnFocus, true);
                window.removeEventListener('keyup', this.isPassengerContainerStillOnFocus, true);
                window.removeEventListener('touchstart', this.isPassengerContainerStillOnFocus, true);
            }
        } else {
            disableBodyScroll();
        }
        this.setState({ listVisible: this.props.showPassengerWarning ? true : !this.state.listVisible });
    }

    isPassengerContainerStillOnFocus(e) {
        if (!this.isElemWithinPassengerContainer(e.target)) {
            this.toggle();
        }
    }

    passengerContainerOnClick() {
        if (this.state.listVisible) {
            if (isMobile()) {
                if (this.props.popUrlContext) {
                    this.props.popUrlContext();
                }
            } else {
                window.removeEventListener('click', this.isPassengerContainerStillOnFocus, true);
                window.removeEventListener('keyup', this.isPassengerContainerStillOnFocus, true);
                window.removeEventListener('touchstart', this.isPassengerContainerStillOnFocus, true);
            }
        } else if (isMobile()) {
            if (this.props.pushUrlContext) {
                this.props.pushUrlContext(URL_PARAM);
            }
            disableBodyScroll();
        } else {
            window.addEventListener('click', this.isPassengerContainerStillOnFocus, true);
            window.addEventListener('keyup', this.isPassengerContainerStillOnFocus, true);
            window.addEventListener('touchstart', this.isPassengerContainerStillOnFocus, true);
        }
        this.setState({ listVisible: this.props.showPassengerWarning ? true : !this.state.listVisible });
    }

    isElemWithinPassengerContainer(elem) {
        return (
            elem !== document.body && elem && (elem.id === 'passenger-container' || this.isElemWithinPassengerContainer(elem.parentElement))
        );
    }

    renderDisplayText() {
        return (
            <span className="no-text-select">
                {this.props.t('passengers.adult', { count: Number.parseInt(this.props.adults) })}
                {this.props.children.length > 0 ? `, ${this.props.t('passengers.child', { count: this.props.children.length })}` : ''}
            </span>
        );
    }

    renderPassengers() {
        return this.state.listVisible ? (
            <Passengers
                adults={this.props.adults}
                children={this.props.children}
                addAdult={this.props.addAdult}
                removeAdult={this.props.removeAdult}
                setAdults={this.props.setAdults}
                addChild={this.props.addChild}
                removeChild={this.props.removeChild}
                setChildren={this.props.setChildren}
                increaseChildAge={this.props.increaseChildAge}
                decreaseChildAge={this.props.decreaseChildAge}
                setChildAge={this.props.setChildAge}
                showPassengerWarning={this.props.showPassengerWarning}
            />
        ) : null;
    }

    renderMobile() {
        return this.state.listVisible ? (
            <div className="fullscreen-list" id="overlay">
                <div className="scrollable-list-container">
                    <h3 className="u-ma16 u-t2">{this.props.t('passengers.title')}</h3>
                    {this.renderPassengers()}
                    <div className="u-mh16 u-mv32">
                        <button className="button button--cta u-size1of1" onClick={this.toggle}>
                            {this.props.t('common:action.done')}
                        </button>
                    </div>
                </div>
            </div>
        ) : null;
    }

    renderDesktop() {
        return this.state.listVisible ? (
            <div className="dropdown-list">
                <div role="dropdown-contents">{this.renderPassengers()}</div>
            </div>
        ) : null;
    }

    render() {
        return (
            <div id="passenger-container" className="dropdown-container">
                <button
                    type="button"
                    onClick={this.passengerContainerOnClick}
                    onKeyUp={this.passengerContainerOnClick}
                    onFocus={this.props.onFocus || null}
                    className="transparent u-r-size1of1"
                    data-testid="passenger-select"
                    aria-expanded={this.state.listVisible}>
                    <div className="dropdown-display">
                        <span data-testid="passenger-text" className="passenger-text no-text-select u-truncate">
                            {this.renderDisplayText()}
                        </span>
                    </div>
                </button>
                {isMobile() ? this.renderMobile() : this.renderDesktop()}
            </div>
        );
    }
}

FlightPassengers.propTypes = {
    t: PropTypes.func.isRequired,
    adults: PropTypes.number.isRequired,
    children: PropTypes.array.isRequired,
    addAdult: PropTypes.func.isRequired,
    removeAdult: PropTypes.func.isRequired,
    setAdults: PropTypes.func.isRequired,
    addChild: PropTypes.func.isRequired,
    removeChild: PropTypes.func.isRequired,
    setChildren: PropTypes.func.isRequired,
    increaseChildAge: PropTypes.func.isRequired,
    decreaseChildAge: PropTypes.func.isRequired,
    setChildAge: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    pushUrlContext: PropTypes.func,
    popUrlContext: PropTypes.func,
    showPassengerWarning: PropTypes.bool,
};

export default withTranslation('search')(FlightPassengers);
